import React, { useState, useEffect, useRef } from 'react';
import { useTenantFromUrl } from '../hooks/useTenantFromUrl';
import api, { rolePermissionAPI } from '../services/api';
import { hasPermission } from '../utils/permissions';
import ContentHeader from '../components/ContentHeader';
import './RolePermissionManager.css';

/** Resources that are gated by tenant feature flags; hide their permissions when the feature is off. */
const FEATURE_RESOURCES = {
  at_packets: 'at_packet',
  checklists: 'checklist',
};

/** Display names for permission categories to reduce ambiguity (resource → label). */
const RESOURCE_DISPLAY_NAMES = {
  documents: 'Client folder (documents)',
  templates: 'DocGen templates',
  /** Referral-scoped tasks on the client Work Tasks page (not the main Dashboard panels). */
  client_work_tasks: 'My To-Dos (client)',
  /** Main app Dashboard (tracking, notifications, checklist panels); API resource is still work_tasks */
  work_tasks: 'Dashboard',
};

/** Friendlier labels for stable permission codes (DB `name` is often legacy wording). */
const PERMISSION_LABEL_OVERRIDES = {
  'client_work_tasks.read': 'View My To-Dos (client page, referral-scoped)',
  'client_work_tasks.create': 'Create My To-Dos (client page)',
  'client_work_tasks.update': 'Update My To-Dos (client page)',
  'client_work_tasks.delete': 'Delete My To-Dos (client page)',
  'work_tasks.view': 'Open Dashboard (tracking work, notifications, checklist panels)',
  'work_tasks.view_all': 'Filter Dashboard by staff — see any service coordinator’s work (mentors / trainers)',
  'work_tasks.complete': 'Complete tracking items on the Dashboard',
  'referrals.update_tracking': 'Update Referral Tracking (Client Tracking)',
};

function RolePermissionManager() {
  useTenantFromUrl();
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [features, setFeatures] = useState({ at_packet: false, checklist: false });
  const [selectedRole, setSelectedRole] = useState(null);
  const [rolePermissions, setRolePermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // 'saving' | 'saved' | 'error'
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const saveTimeoutRef = useRef(null);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createCode, setCreateCode] = useState('');
  const [createName, setCreateName] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [editName, setEditName] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (selectedRole) {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }
      loadRolePermissions(selectedRole.id);
    }
  }, [selectedRole]);

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [rolesRes, permsRes, whoamiRes] = await Promise.all([
        rolePermissionAPI.getAllRoles(),
        rolePermissionAPI.getAllPermissions(),
        api.get('/whoami').catch(() => ({ data: {} })),
      ]);

      const rolesData = rolesRes.data?.data || [];
      const permsData = permsRes.data?.data || [];
      const whoamiFeatures = whoamiRes.data?.features ?? { at_packet: false, checklist: false };

      setRoles(rolesData);
      setPermissions(permsData);
      setFeatures(whoamiFeatures);

      if (rolesData.length > 0) {
        setSelectedRole(rolesData[0]);
      } else {
        setSelectedRole(null);
        setError('No roles found. Create one or check database.');
      }
    } catch (err) {
      console.error('Error loading data:', err);
      console.error('Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status
      });
      setError(`Failed to load roles and permissions: ${err.response?.data?.error || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadRolePermissions = async (roleId) => {
    try {
      const response = await rolePermissionAPI.getRolePermissions(roleId);
      setRolePermissions(response.data.data.map(p => p.id));
    } catch (err) {
      console.error('Error loading role permissions:', err);
      setError('Failed to load role permissions');
    }
  };

  const savePermissions = async (permissionIds) => {
    if (!selectedRole) return;
    try {
      setSaving(true);
      setSaveStatus('saving');
      setError(null);
      await rolePermissionAPI.updateRolePermissions(selectedRole.id, permissionIds);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus(null), 2000);
    } catch (err) {
      console.error('Error saving permissions:', err);
      setError(err.response?.data?.error || 'Failed to save permissions');
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 3000);
    } finally {
      setSaving(false);
    }
  };

  const handlePermissionToggle = (permissionId) => {
    const next = rolePermissions.includes(permissionId)
      ? rolePermissions.filter(id => id !== permissionId)
      : [...rolePermissions, permissionId];
    setRolePermissions(next);

    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      saveTimeoutRef.current = null;
      savePermissions(next);
    }, 400);
  };

  const handleCreateRole = async (e) => {
    e.preventDefault();
    if (!createCode.trim() || !createName.trim()) return;
    try {
      setSaving(true);
      setError(null);
      const res = await rolePermissionAPI.createRole({ code: createCode.trim(), name: createName.trim() });
      const newRole = res.data?.data;
      setShowCreateModal(false);
      setCreateCode('');
      setCreateName('');
      await loadData();
      if (newRole) setSelectedRole(newRole);
      setSuccess('Role created. Assign permissions below.');
      setTimeout(() => setSuccess(null), 4000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create role');
    } finally {
      setSaving(false);
    }
  };

  const openEditModal = () => {
    if (selectedRole) {
      setEditName(selectedRole.name || '');
      setShowEditModal(true);
    }
  };

  const handleEditRole = async (e) => {
    e.preventDefault();
    if (!selectedRole || !editName.trim()) return;
    try {
      setSaving(true);
      setError(null);
      await rolePermissionAPI.updateRole(selectedRole.id, { name: editName.trim() });
      setShowEditModal(false);
      await loadData();
      setSelectedRole(prev => prev ? { ...prev, name: editName.trim() } : null);
      setSuccess('Role name updated.');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update role');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRole = async () => {
    if (!selectedRole) return;
    try {
      setSaving(true);
      setError(null);
      await rolePermissionAPI.deleteRole(selectedRole.id);
      setShowDeleteConfirm(false);
      await loadData();
      setSuccess('Role deleted.');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete role');
    } finally {
      setSaving(false);
    }
  };

  /** Default/system roles can be edited but not deleted */
  const protectedRoleCodes = ['ADMIN', 'MANAGER', 'SERVICE_COORDINATOR', 'DEVELOPER'];
  const isProtectedRole = selectedRole && protectedRoleCodes.includes(selectedRole.code?.toUpperCase());

  // Group permissions by resource; hide resources for features that are off for this tenant
  const permissionsByResource = permissions.reduce((acc, perm) => {
    const featureKey = FEATURE_RESOURCES[perm.resource];
    if (featureKey && !features[featureKey]) {
      return acc;
    }
    if (!acc[perm.resource]) {
      acc[perm.resource] = [];
    }
    acc[perm.resource].push(perm);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="role-permission-manager">
        <div className="loading">Loading roles and permissions...</div>
      </div>
    );
  }

  // Check if user has required permission
  const hasManageRoles = hasPermission('settings.manage_roles');

  if (!hasManageRoles) {
    return (
      <div className="role-permission-manager">
        <div className="alert alert-error">
          <strong>Access Denied</strong>
          <p>You need the 'settings.manage_roles' permission to access this page.</p>
          <p>Please contact your administrator to assign this permission to your role.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="role-permission-manager">
      <ContentHeader
        title="Roles & permissions"
        titleHint="Assign permissions to roles. Changes take effect immediately for users with those roles."
      />

      {error && (
        <div className="alert alert-error">
          <strong>Error:</strong> {error}
          <br />
          <small style={{ marginTop: '8px', display: 'block' }}>
            Check browser console (F12) for details. Make sure you have the 'settings.manage_roles' permission.
          </small>
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          {success}
        </div>
      )}

      <div className="role-permission-content">
        <div className="role-selector">
          <h2>Select Role</h2>
          <div className="role-selector-actions">
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => { setShowCreateModal(true); setError(null); }}
            >
              + Create role
            </button>
            {selectedRole && (
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={openEditModal}
                disabled={saving}
              >
                Edit role
              </button>
            )}
          </div>
          {roles.length === 0 ? (
            <div className="no-roles">
              <p>No roles found. Create one above.</p>
            </div>
          ) : (
            <div className="role-list">
              {roles.map(role => (
                <button
                  key={role.id}
                  className={`role-button ${selectedRole?.id === role.id ? 'active' : ''}`}
                  onClick={() => setSelectedRole(role)}
                  title={role.code}
                >
                  {role.name}
                </button>
              ))}
            </div>
          )}
        </div>

        {selectedRole && (
          <div className="permission-assignment">
            <div className="permission-header">
              <h2>Permissions for: {selectedRole.name}</h2>
              <div className="permission-header-actions">
                {saveStatus && (
                  <span className={`permission-save-status ${saveStatus === 'error' ? 'error' : ''}`}>
                    {saveStatus === 'saving' ? 'Saving...' : saveStatus === 'saved' ? 'Saved' : 'Save failed'}
                  </span>
                )}
                {!isProtectedRole && (
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={() => setShowDeleteConfirm(true)}
                    disabled={saving}
                  >
                    Delete role
                  </button>
                )}
              </div>
            </div>
            <div className="permission-groups">
              {Object.entries(permissionsByResource).map(([resource, perms]) => (
                <div key={resource} className="permission-group">
                  <h3 className="resource-header">
                    {RESOURCE_DISPLAY_NAMES[resource] ?? resource.replace(/_/g, ' ').toUpperCase()}
                  </h3>
                  <div className="permission-list">
                    {perms.map(perm => (
                      <label key={perm.id} className="permission-item" title={perm.code}>
                        <input
                          type="checkbox"
                          checked={rolePermissions.includes(perm.id)}
                          onChange={() => handlePermissionToggle(perm.id)}
                        />
                        <span className="permission-label">
                          {PERMISSION_LABEL_OVERRIDES[perm.code] ?? perm.name}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Create role modal */}
      {showCreateModal && (
        <div className="role-modal-overlay" onClick={() => !saving && setShowCreateModal(false)}>
          <div className="role-modal" onClick={e => e.stopPropagation()}>
            <h3>Create role</h3>
            <form onSubmit={handleCreateRole}>
              <div className="form-group">
                <label htmlFor="create-role-code">Code</label>
                <input
                  id="create-role-code"
                  type="text"
                  value={createCode}
                  onChange={e => setCreateCode(e.target.value)}
                  placeholder="e.g. LEAD_SERVICE_COORDINATOR"
                  required
                />
                <small>Letters, numbers, underscores only. Stored in uppercase.</small>
              </div>
              <div className="form-group">
                <label htmlFor="create-role-name">Display name</label>
                <input
                  id="create-role-name"
                  type="text"
                  value={createName}
                  onChange={e => setCreateName(e.target.value)}
                  placeholder="e.g. Lead Service Coordinator"
                  required
                />
              </div>
              <div className="role-modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCreateModal(false)} disabled={saving}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving || !createCode.trim() || !createName.trim()}>
                  {saving ? 'Creating...' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit role modal */}
      {showEditModal && selectedRole && (
        <div className="role-modal-overlay" onClick={() => !saving && setShowEditModal(false)}>
          <div className="role-modal" onClick={e => e.stopPropagation()}>
            <h3>Edit role: {selectedRole.code}</h3>
            <form onSubmit={handleEditRole}>
              <div className="form-group">
                <label htmlFor="edit-role-name">Display name</label>
                <input
                  id="edit-role-name"
                  type="text"
                  value={editName}
                  onChange={e => setEditName(e.target.value)}
                  required
                />
              </div>
              <div className="role-modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)} disabled={saving}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving || !editName.trim()}>
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {showDeleteConfirm && selectedRole && (
        <div className="role-modal-overlay" onClick={() => !saving && setShowDeleteConfirm(false)}>
          <div className="role-modal" onClick={e => e.stopPropagation()}>
            <h3>Delete role</h3>
            <p>Are you sure you want to delete the role &quot;{selectedRole.name}&quot;? This cannot be undone. If the role is assigned to any users, you must reassign them first.</p>
            <div className="role-modal-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteConfirm(false)} disabled={saving}>
                Cancel
              </button>
              <button type="button" className="btn btn-danger" onClick={handleDeleteRole} disabled={saving}>
                {saving ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default RolePermissionManager;
